import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { TalentHappyPathDataService } from '../../../shared/service/talent-happy-path-data-service';
import { hasAlreadyRecorded, VideoInterviewApiService } from '../service/video-interview-api.service';
import { VideoInterviewFlowService } from '../service/video-interview-flow.service';

/**
 * The step's front door: what the video interview is, and the choice to start it or not.
 *
 * It also decides whether the step applies at all. A talent who has already recorded is not asked
 * again, and that check lands here because the transition into the step has to switch the page
 * synchronously (see `enterVideoInterview`). Nothing renders until the status answer arrives, so no
 * Start button flashes before a skip. An unreadable status shows the step: a missed skip costs one
 * screen, a wrong skip costs a recording.
 */
@Component({
    selector: 'app-video-interview-landing-page',
    imports: [
        MatButton,
        MatProgressSpinnerModule,
        TranslateModule
    ],
    templateUrl: './video-interview-landing-page.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './video-interview-landing-page.component.scss'
})
export class VideoInterviewLandingPageComponent implements OnInit, OnDestroy {

  /** False until the status read settles, so the Start button cannot flash before a skip. */
  offerTheStep = false;

  private readonly subscriptions = new Subscription();

  constructor(private videoInterviewApiService: VideoInterviewApiService,
              private videoInterviewFlowService: VideoInterviewFlowService,
              private dataService: TalentHappyPathDataService) {}

  ngOnInit(): void {
    const personId = this.dataService.talent?.personId;
    if (personId == null) {
      this.offerTheStep = true;
      return;
    }
    this.subscriptions.add(
      this.videoInterviewApiService.readStatus(personId).subscribe({
        next: response => {
          if (hasAlreadyRecorded(response)) {
            this.videoInterviewFlowService.skipVideoInterview();
            return;
          }
          this.offerTheStep = true;
        },
        error: error => {
          console.error('Error reading the video interview status', error);
          this.offerTheStep = true;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  clickStartVideoInterview(): void {
    this.videoInterviewFlowService.startVideoInterview();
  }

  clickIllDoThisLater(): void {
    this.videoInterviewFlowService.doThisLater();
  }
}
